import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import apiPublico from '../services/apiPublico';
import CheckoutMercadoPago from '../components/loja/CheckoutMercadoPago';
import s from './Loja.module.css';

const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');
const arquivoUrl = (caminho) => (caminho?.startsWith('http') ? caminho : `${BACKEND_URL}${caminho}`);

function formatarPreco(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// wa.me exige só dígitos com DDI — assume Brasil (55) quando o número
// configurado veio sem DDI (10/11 dígitos = DDD + fixo/celular).
function linkWhatsapp(numero, texto) {
  let digitos = String(numero || '').replace(/\D/g, '');
  if (digitos.length <= 11) digitos = `55${digitos}`;
  return `https://wa.me/${digitos}?text=${encodeURIComponent(texto)}`;
}

export default function Loja() {
  const { slug } = useParams();

  const [status, setStatus] = useState('carregando'); // carregando | ok | naoEncontrada | indisponivel
  const [loja, setLoja] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [grupoAtivo, setGrupoAtivo] = useState(null); // null = todas
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [produtos, setProdutos] = useState([]);
  const [carregandoProdutos, setCarregandoProdutos] = useState(true);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [qtdModal, setQtdModal] = useState(1);

  // Sacola: seleção local só pra montar a mensagem do pedido — nunca vira
  // pedido/venda no sistema, não soma estoque nem passa pelo backend.
  const [sacola, setSacola] = useState({}); // { [produtoId]: { nome, preco_venda, quantidade } }
  const [sacolaAberta, setSacolaAberta] = useState(false);
  const [checkoutAberto, setCheckoutAberto] = useState(false);
  const [pedidoConfirmado, setPedidoConfirmado] = useState(null); // id do pedido, depois de pago

  useEffect(() => {
    let ativo = true;
    apiPublico.get(`/catalogo/${slug}`)
      .then(({ data }) => {
        if (!ativo) return;
        setLoja(data);
        setStatus('ok');
      })
      .catch((err) => {
        if (!ativo) return;
        setStatus(err.response?.status === 429 ? 'indisponivel' : 'naoEncontrada');
      });
    return () => { ativo = false; };
  }, [slug]);

  useEffect(() => {
    if (status !== 'ok') return;
    apiPublico.get(`/catalogo/${slug}/categorias`)
      .then(({ data }) => setCategorias(data))
      .catch(() => setCategorias([]));
  }, [slug, status]);

  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounced(busca.trim()), 350);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    if (status !== 'ok') return;
    setCarregandoProdutos(true);
    const params = {};
    if (grupoAtivo) params.grupo_id = grupoAtivo;
    if (buscaDebounced) params.busca = buscaDebounced;
    apiPublico.get(`/catalogo/${slug}/produtos`, { params })
      .then(({ data }) => setProdutos(data))
      .catch(() => setProdutos([]))
      .finally(() => setCarregandoProdutos(false));
  }, [slug, status, grupoAtivo, buscaDebounced]);

  const abrirProduto = useCallback((produto) => {
    setQtdModal(1);
    setProdutoSelecionado({ carregando: true, id: produto.id });
    apiPublico.get(`/catalogo/${slug}/produtos/${produto.id}`)
      .then(({ data }) => setProdutoSelecionado(data))
      .catch(() => setProdutoSelecionado(null));
  }, [slug]);

  const adicionarNaSacola = (produto, quantidade, { abrirSacola = true } = {}) => {
    setSacola(prev => {
      const atual = prev[produto.id]?.quantidade || 0;
      return {
        ...prev,
        [produto.id]: { nome: produto.nome, preco_venda: produto.preco_venda, quantidade: atual + quantidade, eh_curso: !!produto.eh_curso },
      };
    });
    if (abrirSacola) {
      setProdutoSelecionado(null);
      setSacolaAberta(true);
    }
  };

  // Quick-add direto no card, sem passar pelo modal de detalhe — só atualiza
  // a barra flutuante, não abre o resumo (facilita adicionar vários seguidos).
  const adicionarRapido = (e, produto) => {
    e.stopPropagation();
    if (produto.esgotado) return;
    adicionarNaSacola(produto, 1, { abrirSacola: false });
  };

  const alterarQuantidadeSacola = (id, delta) => {
    setSacola(prev => {
      const item = prev[id];
      if (!item) return prev;
      const novaQtd = item.quantidade + delta;
      if (novaQtd <= 0) {
        const { [id]: _remover, ...resto } = prev;
        return resto;
      }
      return { ...prev, [id]: { ...item, quantidade: novaQtd } };
    });
  };

  const itensSacola = Object.entries(sacola);
  const totalItens = useMemo(() => itensSacola.reduce((soma, [, it]) => soma + it.quantidade, 0), [sacola]);
  const totalValor = useMemo(
    () => itensSacola.reduce((soma, [, it]) => soma + it.quantidade * Number(it.preco_venda || 0), 0),
    [sacola]
  );

  const mensagemPedido = () => {
    const linhas = itensSacola.map(([, it]) => `${it.quantidade}x ${it.nome} — ${formatarPreco(it.preco_venda)}`);
    if (pedidoConfirmado) {
      return `Olá! Acabei de pagar o pedido #${pedidoConfirmado} pela vitrine ${loja?.nome || ''}:\n\n${linhas.join('\n')}\n\nTotal: ${formatarPreco(totalValor)}`;
    }
    return `Olá! Gostaria de fazer um pedido pela vitrine ${loja?.nome || ''}:\n\n${linhas.join('\n')}\n\nTotal: ${formatarPreco(totalValor)}`;
  };

  const pedidoPago = useCallback((novoPedidoId) => {
    setPedidoConfirmado(novoPedidoId);
    setCheckoutAberto(false);
    setSacolaAberta(false);
  }, []);

  const fecharConfirmacao = () => {
    setPedidoConfirmado(null);
    setSacola({}); // pedido já foi gerado — a sacola local não serve mais pra nada
  };

  if (status === 'carregando') {
    return <div className={s.telaCentral}>Carregando...</div>;
  }
  if (status === 'indisponivel') {
    return <div className={s.telaCentral}>Muitas requisições — tente novamente em instantes.</div>;
  }
  if (status === 'naoEncontrada') {
    return (
      <div className={s.telaCentral}>
        <div className={s.naoEncontrada}>
          <div className={s.naoEncontradaIcone}>🔍</div>
          <h1>Loja não encontrada</h1>
          <p>Este endereço não existe ou o catálogo está temporariamente desativado.</p>
        </div>
      </div>
    );
  }

  const corPrimaria = loja.cor_primaria || '#1565ff';
  const corSecundaria = loja.cor_secundaria || '#0f172a';
  const aceitaPedidoWhatsapp = !!loja.whatsapp;

  return (
    <div className={s.pagina} style={{ '--loja-primaria': corPrimaria, '--loja-secundaria': corSecundaria }}>
      {loja.banner_url && (
        <div className={s.banner} style={{ backgroundImage: `url(${arquivoUrl(loja.banner_url)})` }} />
      )}

      <header className={s.header}>
        {loja.logo_url && <img className={s.logo} src={arquivoUrl(loja.logo_url)} alt={loja.nome} />}
        <div>
          <h1 className={s.nomeLoja}>{loja.nome}</h1>
          {loja.mensagem_boas_vindas && <p className={s.mensagem}>{loja.mensagem_boas_vindas}</p>}
        </div>
      </header>

      <div className={s.buscaWrap}>
        <input
          className={s.buscaInput}
          type="search"
          value={busca}
          onChange={e => setBusca(e.target.value)}
          placeholder="🔍 Buscar produto..."
        />
      </div>

      {categorias.length > 0 && (
        <nav className={s.categorias}>
          <button
            className={`${s.categoriaBtn} ${grupoAtivo === null ? s.categoriaAtiva : ''}`}
            onClick={() => setGrupoAtivo(null)}
          >
            Todos
          </button>
          {categorias.map(cat => (
            <button
              key={cat.id}
              className={`${s.categoriaBtn} ${grupoAtivo === cat.id ? s.categoriaAtiva : ''}`}
              onClick={() => setGrupoAtivo(cat.id)}
            >
              {cat.nome}
            </button>
          ))}
        </nav>
      )}

      <main className={s.conteudo} style={aceitaPedidoWhatsapp && totalItens > 0 ? { paddingBottom: 76 } : undefined}>
        {carregandoProdutos ? (
          <div className={s.vazio}>Carregando produtos...</div>
        ) : produtos.length === 0 ? (
          <div className={s.vazio}>
            {buscaDebounced ? 'Nenhum produto encontrado pra essa busca.' : 'Nenhum produto disponível nesta categoria.'}
          </div>
        ) : (
          <div className={s.grade}>
            {produtos.map(p => (
              <button key={p.id} className={`${s.card} ${p.esgotado ? s.cardEsgotado : ''}`} onClick={() => abrirProduto(p)}>
                {p.destaque && <span className={s.seloDestaque}>★ Destaque</span>}
                <div className={s.cardFoto}>
                  {p.foto_url
                    ? <img src={arquivoUrl(p.foto_url)} alt={p.nome} />
                    : <span className={s.cardFotoVazia}>📦</span>}
                  {p.esgotado && <span className={s.seloEsgotado}>Esgotado</span>}
                </div>
                <div className={s.cardCorpo}>
                  {p.grupo_nome && <span className={s.cardGrupo}>{p.grupo_nome}</span>}
                  <span className={s.cardNome}>{p.nome}</span>
                  <span className={s.cardPreco}>{formatarPreco(p.preco_venda)}</span>
                </div>
                {aceitaPedidoWhatsapp && !p.esgotado && (
                  <span className={s.cardAddRapido} onClick={e => adicionarRapido(e, p)}>+</span>
                )}
              </button>
            ))}
          </div>
        )}
      </main>

      {(loja.telefone || loja.email || loja.endereco) && (
        <footer className={s.footer}>
          {loja.telefone && <span>{loja.telefone}</span>}
          {loja.email && <span>{loja.email}</span>}
          {loja.endereco && <span>{loja.endereco}</span>}
        </footer>
      )}

      {aceitaPedidoWhatsapp && totalItens > 0 && (
        <button className={s.barraSacola} onClick={() => setSacolaAberta(true)}>
          <span>🛍️ {totalItens} {totalItens === 1 ? 'item' : 'itens'} — {formatarPreco(totalValor)}</span>
          <span className={s.barraSacolaAcao}>Ver sacola ›</span>
        </button>
      )}

      {produtoSelecionado && (
        <div className={s.modalFundo} onClick={() => setProdutoSelecionado(null)}>
          <div className={s.modal} onClick={e => e.stopPropagation()}>
            <button className={s.modalFechar} onClick={() => setProdutoSelecionado(null)}>✕</button>
            {produtoSelecionado.carregando ? (
              <div className={s.vazio}>Carregando...</div>
            ) : (
              <>
                <div className={s.modalFotos}>
                  {produtoSelecionado.fotos?.length > 0 ? (
                    produtoSelecionado.fotos.map((f, i) => (
                      <img key={i} src={arquivoUrl(f)} alt={produtoSelecionado.nome} />
                    ))
                  ) : (
                    <span className={s.cardFotoVazia}>📦</span>
                  )}
                </div>
                {produtoSelecionado.grupo_nome && <span className={s.cardGrupo}>{produtoSelecionado.grupo_nome}</span>}
                <h2 className={s.modalNome}>{produtoSelecionado.nome}</h2>
                <span className={s.modalPreco}>{formatarPreco(produtoSelecionado.preco_venda)}</span>
                {produtoSelecionado.esgotado && <span className={s.seloEsgotado} style={{ position: 'static', display: 'inline-block', marginTop: 6 }}>Esgotado</span>}
                {(produtoSelecionado.descricao || produtoSelecionado.descricao_resumida) && (
                  <p className={s.modalDescricao}>
                    {produtoSelecionado.descricao || produtoSelecionado.descricao_resumida}
                  </p>
                )}

                {aceitaPedidoWhatsapp && !produtoSelecionado.esgotado && (
                  <div className={s.modalAdicionar}>
                    <div className={s.qtdStepper}>
                      <button onClick={() => setQtdModal(q => Math.max(1, q - 1))}>−</button>
                      <span>{qtdModal}</span>
                      <button onClick={() => setQtdModal(q => q + 1)}>+</button>
                    </div>
                    <button
                      className={s.btnAdicionar}
                      onClick={() => adicionarNaSacola(produtoSelecionado, qtdModal)}
                    >
                      Adicionar à sacola
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {sacolaAberta && (
        <div className={s.modalFundo} onClick={() => setSacolaAberta(false)}>
          <div className={s.modal} onClick={e => e.stopPropagation()}>
            <button className={s.modalFechar} onClick={() => setSacolaAberta(false)}>✕</button>
            <h2 className={s.modalNome}>Sua sacola</h2>

            {itensSacola.length === 0 ? (
              <p className={s.vazio}>Sua sacola está vazia.</p>
            ) : (
              <>
                <div className={s.listaSacola}>
                  {itensSacola.map(([id, it]) => (
                    <div key={id} className={s.itemSacola}>
                      <div className={s.itemSacolaInfo}>
                        <span className={s.itemSacolaNome}>{it.nome}</span>
                        <span className={s.itemSacolaPreco}>{formatarPreco(it.preco_venda)}</span>
                      </div>
                      <div className={s.qtdStepper}>
                        <button onClick={() => alterarQuantidadeSacola(id, -1)}>−</button>
                        <span>{it.quantidade}</span>
                        <button onClick={() => alterarQuantidadeSacola(id, 1)}>+</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className={s.totalSacola}>
                  <span>Total</span>
                  <span>{formatarPreco(totalValor)}</span>
                </div>

                {loja.pagamento_online && (
                  <button className={s.btnPagar} onClick={() => setCheckoutAberto(true)}>
                    💳 Pagar agora (PIX, débito ou crédito)
                  </button>
                )}
                <a
                  className={loja.pagamento_online ? s.btnPagarSecundario : s.btnWhatsapp}
                  href={linkWhatsapp(loja.whatsapp, mensagemPedido())}
                  target="_blank"
                  rel="noreferrer"
                >
                  Pedir no WhatsApp
                </a>
                <p className={s.ajudaSacola}>
                  {loja.pagamento_online
                    ? 'Pagando agora, seu pedido já entra confirmado pra loja preparar.'
                    : 'Isso só abre uma conversa no WhatsApp com sua lista — o pedido é combinado direto com a loja, sem pagamento por aqui.'}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {checkoutAberto && (
        <CheckoutMercadoPago
          slug={slug}
          itensSacola={itensSacola}
          total={totalValor}
          onFechar={() => setCheckoutAberto(false)}
          onPedidoConfirmado={pedidoPago}
        />
      )}

      {pedidoConfirmado && (
        <div className={s.modalFundo}>
          <div className={s.modal}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem' }}>✅</div>
              <h2 className={s.modalNome}>Pedido #{pedidoConfirmado} confirmado!</h2>
              <p className={s.ajudaSacola} style={{ fontSize: '0.85rem' }}>
                Pagamento aprovado — a loja já recebeu seu pedido.
              </p>
              {aceitaPedidoWhatsapp && (
                <a
                  className={s.btnWhatsapp}
                  href={linkWhatsapp(loja.whatsapp, mensagemPedido())}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setTimeout(fecharConfirmacao, 300)}
                >
                  Avisar a loja no WhatsApp
                </a>
              )}
              <button className={s.btnPagarSecundario} onClick={fecharConfirmacao} style={{ marginTop: 8 }}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
